import { throttle } from "lodash";
import {
	type ComponentProps,
	type CSSProperties,
	type FC,
	useEffect,
	useRef,
} from "react";
import { Icon } from "../common/Icon";
import { Card } from "./Card";

import "./Stepper.css";

export type StepSettings = {
	title: string;
	icon?: ComponentProps<typeof Icon>["name"];
	iconSize?: number;
	complete?: boolean;
	substeps?: Array<string>;
	currentSubStep?: number;
};

interface StepProps extends StepSettings {
	active?: boolean;
}

const Step: FC<Readonly<StepProps>> = ({
	title,
	icon,
	iconSize,
	active,
	complete,
}) => {
	return (
		<div
			className={`step${active ? " active" : ""}${complete ? " complete" : ""}`}
		>
			<figure>{icon && <Icon name={icon} size={iconSize ?? 24} />}</figure>

			<span>{title}</span>
		</div>
	);
};

interface Props {
	steps: Array<StepSettings>;
	current: number;
	gap?: number;
	iconSize?: number;
}

export const Stepper: FC<Readonly<Props>> = ({
	steps,
	current,
	gap = 1,
	iconSize = 24,
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleResize = () => {
			if (!ref.current) {
				return;
			}

			const wrapperWidth = ref.current.offsetWidth;

			ref.current.style.setProperty("--stepper-width", `${wrapperWidth}px`);
			ref.current.style.setProperty("--stepper-count", steps.length.toString());
		};

		const throttledHandleResize = throttle(handleResize, 300);

		window.addEventListener("resize", throttledHandleResize);
		handleResize();

		return () => {
			window.removeEventListener("resize", throttledHandleResize);
		};
	}, [steps]);

	return (
		<Card className="stepper">
			<Card.Body>
				<div
					ref={ref}
					style={
						{
							"--stepper-gap": `${gap}rem`,
							"--stepper-icon-size": `${iconSize}px`,
						} as CSSProperties
					}
					className="wrapper"
				>
					{steps.map((step, index) => (
						<Step
							key={`step-${index}-${step.title}`}
							title={step.title}
							icon={step.complete ? "verified" : step.icon}
							iconSize={iconSize}
							active={current === index}
							complete={step.complete}
						/>
					))}
				</div>
			</Card.Body>
		</Card>
	);
};
